import useSWR, { SWRResponse } from "swr";
import type {
  CompilationThemeId,
  CompilationTheme,
} from "@memento/types/compilation-theme";
import {
  getLoadingStatus,
  LoadingStatus,
  useUnauthenticatedFetcher,
} from "src/libs/fetcher";
import { addUrlParams } from "src/libs/urlLib";
import { UseCompilationThemesCacheKey } from "./useCompilationThemes";
import { useCachedResultData } from "src/hooks/useCachedResultData";
import { useMemo } from "react";

export const useCompilationTheme = (themeId: CompilationThemeId) => {
  // if we've already fetched all of the themes/occasions (i.e. they're already
  // cached), we don't need to fetch an individual one, just get it from the
  // cache.

  const themes = useCachedResultData<CompilationTheme[]>(
    UseCompilationThemesCacheKey
  );

  const theme = useMemo(() => {
    return themes?.length
      ? themes.find((x) => x.themeId === themeId)
      : undefined;
  }, [themes, themeId]);

  const fetcher = useUnauthenticatedFetcher();

  // set to null if we already found the theme, so that the fetcher won't fetch
  const key = theme ? null : addUrlParams(`/compilation-themes/${themeId}`);

  const { data, error }: SWRResponse<CompilationTheme, Error> = useSWR(
    key,
    (url: string) => fetcher(url)
  );

  return {
    theme,
    error,
    status: theme ? LoadingStatus.SUCCESS : getLoadingStatus(key, data, error),
  };
};
